import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, mensajeDe, type Organizador, type Yo } from '../../services/api';
import { Aviso, Boton, Campo, Cargando, ENTRADA, Tarjeta, Titulo } from '../../components/ui';
import { CambiarPin } from './Entrada';

/** Los 2 o 3 organizadores. Todos pueden lo mismo; cada uno con su PIN. */
export function Organizadores({ yo }: { yo: Yo }) {
  const [lista, setLista] = useState<Organizador[] | null>(null);
  const [error, setError] = useState('');
  const [nombre, setNombre] = useState('');
  // El PIN provisorio se muestra UNA vez: la base solo guarda el hash.
  const [pinNuevo, setPinNuevo] = useState<{ nombre: string; pin: string } | null>(null);

  const recargar = useCallback(async () => {
    try {
      setLista((await api.organizadores()).organizadores);
    } catch (e) {
      setError(mensajeDe(e));
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const accion = async (hacer: () => Promise<void>) => {
    setError('');
    try {
      await hacer();
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    }
  };

  const alta = (e: FormEvent) => {
    e.preventDefault();
    void accion(async () => {
      const r = await api.altaOrganizador(nombre);
      setPinNuevo({ nombre: r.organizador.nombre, pin: r.pin_provisorio });
      setNombre('');
    });
  };

  if (!lista) return error ? <Aviso tono="error">{error}</Aviso> : <Cargando />;

  return (
    <>
      <Tarjeta>
        <Titulo extra={yo.organizacion}>Organizadores</Titulo>
        {error && <Aviso tono="error">{error}</Aviso>}
        {pinNuevo && (
          <div className="mb-3">
            <Aviso tono="bien">
              PIN provisorio de {pinNuevo.nombre}: <b className="font-mono text-lg tracking-widest">{pinNuevo.pin}</b>. Pasáselo en
              persona o por mensaje: se ve una sola vez. Al entrar va a tener que elegir uno propio.
            </Aviso>
          </div>
        )}
        <ul className="flex flex-col">
          {lista.map((o) => (
            <li key={o.id} className="flex items-center gap-2 border-t border-(--color-borde) py-2.5 first:border-t-0">
              <span className={`flex-1 font-semibold ${o.activo ? '' : 'text-(--color-apagado) line-through'}`}>
                {o.nombre}
                {o.id === yo.organizador.id && <span className="ml-2 text-xs font-normal text-(--color-apagado)">(vos)</span>}
                {o.activo === 1 && o.pin_provisorio === 1 && (
                  <span className="ml-2 text-xs font-normal text-(--color-alerta)">todavía no entró</span>
                )}
              </span>
              {o.id !== yo.organizador.id && (
                <>
                  {o.activo === 1 && (
                    <Boton
                      chico
                      tipo="texto"
                      onClick={() =>
                        accion(async () => {
                          const r = await api.resetearPin(o.id);
                          setPinNuevo({ nombre: o.nombre, pin: r.pin_provisorio });
                        })
                      }
                    >
                      Nuevo PIN
                    </Boton>
                  )}
                  <Boton chico tipo="texto" onClick={() => accion(async () => void (await api.activarOrganizador(o.id, o.activo !== 1)))}>
                    {o.activo ? 'Desactivar' : 'Reactivar'}
                  </Boton>
                </>
              )}
            </li>
          ))}
        </ul>

        <form onSubmit={alta} className="mt-4 flex items-end gap-2">
          <div className="flex-1">
            <Campo etiqueta="Sumar organizador">
              <input className={ENTRADA} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre" />
            </Campo>
          </div>
          <Boton type="submit" deshabilitado={nombre.trim().length < 2}>
            Sumar
          </Boton>
        </form>
      </Tarjeta>

      <Tarjeta>
        <Titulo>Cambiar mi PIN</Titulo>
        <CambiarPin alTerminar={() => undefined} />
      </Tarjeta>
    </>
  );
}
